import Map from '@arcgis/core/Map';
import * as reactiveUtils from '@arcgis/core/core/reactiveUtils';
import {
  ExtendedMap,
  LegendParams,
  Plugin
} from '@jeppesen-foreflight/jmkw-types';
import {
  BehaviorSubject,
  NEVER,
  Observable,
  dematerialize,
  distinctUntilChanged,
  map,
  materialize,
  startWith,
  switchMap,
  timer
} from 'rxjs';
import { TwcProduct } from './twc-products';
import { TwcTime } from './twc-time';

// Use this constant to delay the update (in seconds)
const UPDATE_DELAY_SECONDS = 0;

export type TwcTimeUnit = 'seconds' | 'milliseconds';

const TWC_TIME_UNIT_MULTIPLIERS: Record<TwcTimeUnit, number> = {
  seconds: 1000,
  milliseconds: 1
};

export const twcTimeToEpochMilliseconds = (
  time: string | number | undefined,
  unit: TwcTimeUnit
): number => {
  const epochTime = Number(time);
  return Number.isFinite(epochTime)
    ? epochTime * TWC_TIME_UNIT_MULTIPLIERS[unit]
    : Number.NEGATIVE_INFINITY;
};

export const buildPauseOnVisiblitilyTsInterval$ = (
  twcProduct: TwcProduct,
  map: Map
): Observable<number> => {
  const interval$ = minutesToSecondsInterval$(
    twcProduct.timeSlicePeriodInMinutes
  );

  const visible$ = new BehaviorSubject<boolean>(true);
  const pausableInterval$ = visible$.pipe(
    distinctUntilChanged(),
    switchMap((visible) => (visible ? interval$.pipe(materialize()) : NEVER)),
    dematerialize()
  );
  reactiveUtils.watch(
    () =>
      map.layers.find((layer) => layer.id === twcProduct.defaultProductId)
        ?.visible,
    (visible) => {
      visible$.next(!!visible);
    }
  );

  // This observable emits the ts value (in seconds) to update the tile layer
  return pausableInterval$;
}; // END: buildPauseOnVisiblitilyTsInterval$()

// This will return the ts value in seconds with an added delay.
const minutesToSecondsInterval$ = (
  slicePeriodInMinutes: number
): Observable<number> => {
  const minutesInSeconds = slicePeriodInMinutes * 60; // slice period in seconds

  const nowInSeconds = Math.floor(Date.now() / 1000);
  const latestSlicePeriodInSeconds =
    Math.floor(nowInSeconds / minutesInSeconds) * minutesInSeconds;
  // Instead of firing exactly at the next slice, add the fixed delay
  const nextTriggerTimeInSeconds =
    latestSlicePeriodInSeconds + minutesInSeconds + UPDATE_DELAY_SECONDS;

  const secondsInterval$ = timer(
    (nextTriggerTimeInSeconds - nowInSeconds) * 1000,
    minutesInSeconds * 1000
  );

  return secondsInterval$.pipe(
    map(() => Math.floor(Date.now() / 1000)),
    startWith(latestSlicePeriodInSeconds + UPDATE_DELAY_SECONDS)
  );
}; // END: minutesToSecondsInterval$()

export const splitStringTime = (time: string | number) => {
  const timeSeparatorsRegExp = /[-:/]/;
  return `${time}`.split(timeSeparatorsRegExp);
};

export const filterAndSortTimeMap = (
  timeMap: ExtendedMap<TwcTime>,
  unit: TwcTimeUnit = 'milliseconds'
): ExtendedMap<TwcTime> => {
  const now = Date.now();
  const filteredMap = timeMap.filter(({ start, end }) => {
    return twcTimeToEpochMilliseconds(end ?? start, unit) >= now;
  });

  // If filtering results in empty map, return original timeMap
  const resultMap = filteredMap.size ? filteredMap : timeMap;

  // Sort the result map
  return resultMap.sort((a, b) => a[0].localeCompare(b[0]));
};

export const updateLegendTitleBarInfo = (
  legend: LegendParams,
  tsInSeconds: number,
  hasData = false
) => {
  if (hasData) {
    const twcTime = new TwcTime(tsInSeconds);
    legend.legendTitleBarInfo?.set(`${twcTime.stringify(1, 'dd/HH:mm')} UTC`);
  } else {
    legend.legendTitleBarInfo?.set(`NO DATA AVAILABLE`);
  }
}; // END: updateLegendTitleBarInfo()

export const configurePlugin = (plugin: Plugin) => {
  if (plugin.layer) {
    plugin.layer.visible = false;
    plugin.layer.opacity = 0.5;
  }
  if (plugin.children) {
    plugin.children.forEach((child) => configurePlugin(child));
  }
};

export const getLatestTimeFromMap = (
  timeMap: ExtendedMap<TwcTime>
): TwcTime | null => {
  if (timeMap.size === 0) return null;

  // Convert to array, sort by end time, and get the latest
  const times = Array.from(timeMap.values());

  const timeSortFn = (a: TwcTime, b: TwcTime): number => {
    const endA = Number(a.end);
    const endB = Number(b.end);
    return endB - endA; // Sort in descending order
  };

  const sortedTimes = times.sort(timeSortFn);
  return sortedTimes[0] || null;
};
